/**
 * 시스템 공지사항 API (어드민)
 *
 * 백엔드 API 명세서(BACKEND_API_SPEC.md §1) 기반:
 * - GET    /api/admin/announcements
 * - GET    /api/admin/announcements/{id}
 * - POST   /api/admin/announcements
 * - PUT    /api/admin/announcements/{id}
 * - PATCH  /api/admin/announcements/{id}/flags
 * - DELETE /api/admin/announcements/{id}
 * - POST   /api/admin/announcements/upload-image
 */
import { apiClient } from './client';

export type AnnouncementTargetApp = 'BUSINESS' | 'GUARDIAN';

export type AnnouncementCategory =
  | 'SYSTEM'
  | 'MAINTENANCE'
  | 'EVENT'
  | 'GENERAL';

export interface Announcement {
  id: number;
  targetApp: AnnouncementTargetApp;
  category: AnnouncementCategory;
  title: string;
  contentHtml: string;
  isImportant: boolean;
  isPinned: boolean;
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  createdAt: string;
  updatedAt?: string | null;
  createdBy?: number | null;
  updatedBy?: number | null;
}

export interface AnnouncementCreateInput {
  targetApp: AnnouncementTargetApp;
  category: AnnouncementCategory;
  title: string;
  contentHtml: string;
  isImportant?: boolean;
  isPinned?: boolean;
  displayStartAt?: string | null;
  displayEndAt?: string | null;
}

export interface AnnouncementUpdateInput {
  targetApp?: AnnouncementTargetApp;
  category?: AnnouncementCategory;
  title?: string;
  contentHtml?: string;
  isImportant?: boolean;
  isPinned?: boolean;
  displayStartAt?: string | null;
  displayEndAt?: string | null;
  clearDisplayStart?: boolean;
  clearDisplayEnd?: boolean;
}

export interface GetAnnouncementsParams {
  page?: number;
  size?: number;
  targetApp?: AnnouncementTargetApp | 'ALL';
  category?: AnnouncementCategory | 'ALL';
  search?: string;
}

export interface AnnouncementPage {
  content: Announcement[];
  totalElements: number;
  totalPages: number;
  size: number;
  number: number;
  first: boolean;
  last: boolean;
}

export interface AnnouncementImageUploadResult {
  url: string;
  width?: number | null;
  height?: number | null;
}

const BASE = '/admin/announcements';

/** 'ALL' 필터는 query에서 제외 (백엔드는 required=false, null이면 필터 무시) */
function cleanFilter<T extends string>(v: T | 'ALL' | undefined): T | undefined {
  return v && v !== 'ALL' ? (v as T) : undefined;
}

export const announcementsApi = {
  getAnnouncements: async (
    params: GetAnnouncementsParams = {},
  ): Promise<AnnouncementPage> => {
    const res = await apiClient.get<AnnouncementPage>(BASE, {
      params: {
        page: params.page ?? 0,
        size: params.size ?? 10,
        targetApp: cleanFilter(params.targetApp),
        category: cleanFilter(params.category),
        search: params.search?.trim() || undefined,
      },
    });
    return res.data;
  },

  getAnnouncementDetail: async (id: number): Promise<Announcement> => {
    const res = await apiClient.get<Announcement>(`${BASE}/${id}`);
    return res.data;
  },

  createAnnouncement: async (
    input: AnnouncementCreateInput,
  ): Promise<Announcement> => {
    const res = await apiClient.post<Announcement>(BASE, input);
    return res.data;
  },

  updateAnnouncement: async (
    id: number,
    input: AnnouncementUpdateInput,
  ): Promise<Announcement> => {
    const res = await apiClient.put<Announcement>(`${BASE}/${id}`, input);
    return res.data;
  },

  updateAnnouncementFlags: async (
    id: number,
    flags: { isImportant?: boolean; isPinned?: boolean },
  ): Promise<Announcement> => {
    const res = await apiClient.patch<Announcement>(`${BASE}/${id}/flags`, flags);
    return res.data;
  },

  deleteAnnouncement: async (id: number): Promise<void> => {
    await apiClient.delete<void>(`${BASE}/${id}`);
  },

  /**
   * RichTextEditor 이미지 업로드 → 응답 URL 을 <img src> 로 사용.
   * 백엔드가 multipart/form-data 로 받음. apiClient.upload 가 자동 처리.
   */
  uploadImage: async (file: File): Promise<AnnouncementImageUploadResult> => {
    const res = await apiClient.upload<AnnouncementImageUploadResult>(
      `${BASE}/upload-image`,
      file,
    );
    return res.data;
  },
};
